import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface HotelsListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Hotel>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (hotel: Hotel) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /hotels
   */
  baseUrl?: string;
}

/**
 * Reusable hotels list component
 * Pass a custom config with modified actions to customize behavior
 */
export function HotelsList({
  config,
  baseUrl = '/hotels',
  defaultWhere,
  onItemClick,
  className,
}: HotelsListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (hotel: Hotel) => {
      if (onItemClick) {
        onItemClick(hotel);
      } else {
        navigate(`/hotels/${hotel.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || hotelCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        getValueFromItem={(hotel: Hotel) => hotel.id}
        getLabelFromItem={(hotel: Hotel) => hotel.name}
      />
    </div>
  );
}
